import DynamicForm, { DynamicFormRef, makeFormInputsUneditable } from "@mavichpe/dynamic-forms";
import { useEffect, useMemo, useRef, useState } from "react";
import LoginFormConfig from "./form";
import { useAppDispatch } from "store/hooks";
import { fetchSuscription, SuscriptionDataType, updateSuscription } from "@/api/SuscriptionsApi";
import { SuscriptionFormData } from "@/formTypes/ProfileFormDataType";
import { showAlert } from "@utils/Alert";
import { setSuscriptionData } from "@/store/slices/suscriptionSlides";
import { isFreeEmailDomain } from "@utils/Constants";
import { Alert, Button } from "reactstrap";
import { OpenAddProductPubSub } from "@utils/pubsubs/PubSub";
import AddProductToDocumentOffCanvas from "@components/layout/Offcanvas/AddProductToDocumentOffCanvas";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ConfirmationConfig } from "../GenericForm";
import { ProductApi } from "@/api/ProductsApi";
import { buildProductOptionLabel } from "@utils/ProductLabel";
import { omitProperties } from "@utils/Utils";

type ProfileFormProps = {
  suscriptionId?: string
}

/**
 * Campos del perfil que se llenan eligiendo un producto. Como los dos comparten un unico
 * offcanvas de busqueda, hay que recordar cual de ellos lo abrio: sin esto, elegir el
 * producto de envio sobrescribiria el de intereses.
 *
 * `name` es el campo que se persiste (el id del producto, en un input oculto) y
 * `labelField` el input visible de solo lectura, excluido del submit, donde se muestra
 * el codigo y la descripcion.
 */
const PRODUCT_PICKER_FIELDS: { name: keyof SuscriptionFormData, labelField: string, successMessage: string }[] = [
  { name: "interest_product_id", labelField: "interest_product_label", successMessage: "El producto para la generacion de intereses ha sido seleccionado correctamente" },
  { name: "shipping_product_id", labelField: "shipping_product_label", successMessage: "El producto para el cobro del envio ha sido seleccionado correctamente" },
]

const PRODUCT_LABEL_FIELDS = PRODUCT_PICKER_FIELDS.map(f => f.labelField)

/** Lo minimo que se necesita para rotular un producto ya guardado. */
const PRODUCT_LABEL_SELECTION_SET = ["id", "codigo", "descripcion_fact"] as const

const ProfileForm = (props: ProfileFormProps) => {
  const { suscriptionId } = props
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef<DynamicFormRef>(null);
  const [suscription, setSuscription] = useState<SuscriptionDataType>({});
  /**
   * Semilla del formulario: la suscripcion mas los labels de producto, que no son campos
   * de la suscripcion. Va aparte de `suscription` porque DynamicForm reinicia todo su
   * estado cada vez que cambia la REFERENCIA de `externalFormState`; asi las ediciones
   * que actualizan `suscription` (email) no borran lo que el usuario venia escribiendo.
   */
  const [formSeed, setFormSeed] = useState<Record<string, unknown>>({});
  const showFreeEmailWarning = isFreeEmailDomain(suscription?.email) && !suscription?.use_easypos_email;

  /**
   * El perfil solo guarda el id del producto, asi que al abrir la pantalla hay que ir a
   * buscar su codigo para poder mostrarlo. Si el producto ya no existe se cae al id
   * crudo: es feo, pero deja ver que hay algo configurado que conviene revisar.
   */
  const loadProductLabels = async (data: SuscriptionDataType): Promise<Record<string, string>> => {
    const labels = await Promise.all(
      PRODUCT_PICKER_FIELDS.map(async ({ name, labelField }) => {
        const productId = data?.[name];
        if (!productId) {
          return [labelField, ""] as const;
        }
        try {
          const product = await ProductApi.get(`${productId}`, { selectionSet: PRODUCT_LABEL_SELECTION_SET });
          return [labelField, buildProductOptionLabel(product) || `${productId}`] as const;
        } catch {
          return [labelField, `${productId}`] as const;
        }
      })
    );
    return Object.fromEntries(labels);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchSuscription(suscriptionId);
        // Se resuelven los labels ANTES de sembrar el formulario para aplicar el estado
        // externo una sola vez, ya rotulado.
        const productLabels = await loadProductLabels(data);
        setSuscription(data);
        setFormSeed({ ...data, ...productLabels });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadData();
  }, []);

  const onInputChange = (name: keyof SuscriptionFormData, value: any) => {
    if (name === "email" || name === "use_easypos_email") {
      setSuscription(prev => ({ ...prev, [name]: value }));
    }
  }

  const updateProfile = async (rawFormData: Partial<SuscriptionFormData>) => {
    setIsLoading(true);
    // Los labels de producto son solo de pantalla y no existen en el modelo: aunque van
    // marcados como `exludeFromSubmit`, cualquier key desconocida rompe el mutation.
    const formData = omitProperties(rawFormData, PRODUCT_LABEL_FIELDS) as Partial<SuscriptionFormData>;
    try {
      if (!formData?.id) {
        // const response = await createSuscription(formData as SuscriptionDataType);
      } else {
        const suscriptionData = await updateSuscription(formData.id, formData as SuscriptionDataType);
        if (!suscriptionId) {
          dispatch(setSuscriptionData(suscriptionData))
        }
      }
      showAlert('success', 'La informacion de su perfil ha sido almacenado correctamente');
    } catch (err) {
      showAlert('danger', 'No fue posible almacenar la informacion de su perfil');
    } finally {
      setIsLoading(false);
    }
  }

  const productTargetFieldRef = useRef<keyof SuscriptionFormData>("interest_product_id");

  const clickSearchProduct = (targetField: keyof SuscriptionFormData) => {
    productTargetFieldRef.current = targetField;
    OpenAddProductPubSub.publish({ openOffCanvas: true })
  }

  /**
   * El boton para abrir el buscador y el bloqueo de edicion van sobre los inputs de
   * display, no sobre los ids ocultos. Se construye una config nueva en vez de mutar el
   * singleton importado, que de otro modo se queda con el suffix entre montajes.
   */
  const formConfig = useMemo(() => {
    const config = {
      ...LoginFormConfig,
      sections: LoginFormConfig.sections.map(section => ({
        ...section,
        fields: section.fields.map(row =>
          row.map(field => {
            const picker = PRODUCT_PICKER_FIELDS.find(f => f.labelField === field.name)
            if (!picker) {
              return field
            }
            return {
              ...field,
              suffix: (<Button type="button" onClick={() => clickSearchProduct(picker.name)} color="primary" size="sm">Selecciona Producto</Button>),
            }
          })
        ),
      })),
    }
    return makeFormInputsUneditable(config, PRODUCT_LABEL_FIELDS)
  }, [])

  const onProductSelect = (product: EDocumentProductDataType) => {
    if (product.productId) {
      const targetField = productTargetFieldRef.current;
      const target = PRODUCT_PICKER_FIELDS.find(f => f.name === targetField)
      const label = buildProductOptionLabel({ codigo: product.codigo, descripcion_fact: product.description })

      formRef.current?.setFormValue(targetField, product.productId, "String")
      if (target) {
        formRef.current?.setFormValue(target.labelField, label, "String")
      }

      showAlert("success", target?.successMessage || "El producto ha sido seleccionado correctamente")
      OpenAddProductPubSub.publish({ openOffCanvas: false })
    }
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscription || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea actualizar la informacion de su perfil"
      }
    }
  }

  return (
    <>
      {showFreeEmailWarning && (
        <Alert color="warning">
          Por la configuración de seguridad de los servidores de correo, no es recomendable utilizar
          correos electrónicos que no sean de un dominio personalizado (Hotmail, Yahoo, Gmail, etc.).
          La recomendación es activar la opción "Usar correo de Easypos para envío de documentos" para
          que los correos se envíen desde la dirección de Easypos y no sean enviados a spam.
        </Alert>
      )}
      <DynamicForm<SuscriptionFormData>
        id="userProfile"
        ref={formRef}
        confirmationConfig={getCreateConfirmation()}
        externalFormState={formSeed}
        formConfig={formConfig}
        isLoading={isLoading}
        onInputChange={onInputChange}
        onSubmit={updateProfile} >
      </DynamicForm>

      <AddProductToDocumentOffCanvas
        hideTemporal={true}
        hideTemplate={true}
        productSelection={true}
        defaultSearchType="general"
        onProductSelect={onProductSelect}
      >
      </AddProductToDocumentOffCanvas>
    </>
  )
}

export default ProfileForm;